import { DragDropModule } from '@angular/cdk/drag-drop';
import { ObserversModule } from '@angular/cdk/observers';
import { CommonModule as AngularCommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RivModule } from '@rivet-health/design-system';
import { AvatarModule } from '../avatar/module';
import { CalloutModule } from '../callout/module';
import { ComponentsModule } from '../components/module';
import { CoreModule } from '../core/module';
import { InteractionModule } from '../interaction/module';
import { LoadModule } from '../load/module';
import { ScrollModule } from '../scroll/module';
import { StateModule } from '../state/module';
import { TranslationModule } from '../translation/translation.module';
import { AddressInputComponent } from './address-input.component';
import { AutoCompleteListComponent } from './autocomplete-list.component';
import { AutocompleteDirective } from './autocomplete.directive';
import { BulkChipInputComponent } from './bulk-chip-input.component';
import { BulkChipComponent } from './bulk-chip.component';
import { ChipDropdownComponent } from './chip-dropdown.component';
import { ChipInputComponent } from './chip-input.component';
import { ChipSingleComponent } from './chip-single.component';
import { ChipComponent } from './chip.component';
import { ChoiceFieldComponent } from './choice-field.component';
import { DeprecatedDatePickerComponent } from './date-picker.component';
import { DateTextFieldComponent } from './date-text-field.component';
import { EnhancedSelectComponent } from './enhanced-select.component';
import { FileDropDirective } from './filedrop.directive';
import { FormStatusDirective } from './form-status.directive';
import { FormStatusPipe } from './form-status.pipe';
import { FuzzyHighlightComponent } from './fuzzy-highlight.component';
import { InputAccessorDirective } from './input-accessor.directive';
import { InputFormStatusDirective } from './input-form-status.directive';
import { DeprecatedIntFieldComponent } from './int-field.component';
import { LabeledFieldHospitalComponent } from './labeled-field-hospital.component';
import { DeprecatedLabeledFieldComponent } from './labeled-field.component';
import { MultiSelectDropdownFieldComponent } from './multi-select-dropdown-field.component';
import { MultiSelectDropdownComponent } from './multi-select-dropdown.component';
import { MultiSelectNodeComponent } from './multi-select-node.component';
import { MultiSelectNumberComparatorComponent } from './multi-select-number-comparator.component';
import { MultiSelectComponent } from './multi-select.component';
import { OptionFilterComponent } from './option-filter.component';
import { OptionSelectNodeComponent } from './option-select-node.component';
import { OptionSelectNodeDirective } from './option-select-node.directive';
import { OptionSelectComponent } from './option-select.component';
import { EmptyOptionPipe, OptionsPipe, StringOptionsPipe } from './options';
import { PercentValuePipe } from './percent-value.pipe';
import { PhoneMaskDirective } from './phone-mask.directive';
import { RangeSliderFieldComponent } from './range-slider-field.component';
import { RangeSliderComponent } from './range-slider.component';
import { DeprecatedSearchFieldComponent } from './search-field.component';
import { SelectDropdownComponent } from './select-dropdown.component';
import { SelectFieldComponent } from './select-field.component';
import { DeprecatedSingleSelectDropdownFieldComponent } from './single-select-dropdown-field.component';
import { DeprecatedSingleSelectDropdownComponent } from './single-select-dropdown.component';
import { SingleSelectNodeComponent } from './single-select-node.component';
import { SingleSelectNodeDirective } from './single-select-node.directive';
import { SingleSelectComponent } from './single-select.component';
import { SubmitErrorComponent } from './submit-error.component';
import { TaxIdMaskDirective } from './tax-id-mask.directive';
import { TaxIdPipe } from './tax-id.pipe';
import { DeprecatedTextFieldComponent } from './text-field.component';
import { TextareaFieldComponent } from './textarea-field.component';
import { TreeMultiSelectCheckboxComponent } from './tree-multi-select-checkbox.component';
import { TreeMultiSelectDropdownFieldComponent } from './tree-multi-select-dropdown-field.component';
import { TreeMultiSelectDropdownComponent } from './tree-multi-select-dropdown.component';
import { TreeMultiSelectNodeComponent } from './tree-multi-select-node.component';
import { TreeMultiSelectComponent } from './tree-multi-select.component';
import { ValidationErrorComponent } from './validation-error.component';

// this module is a clone of ./module.ts with the exception that
// it doesn't declare AutoCompleteTextboxComponent.
// This is because that component depends on a weird webpack-ism
// for importing web workers that jest doesn't play nice with.
// any spec file importing the InputModule should use this module instead.
@NgModule({
  declarations: [
    AddressInputComponent,
    AutocompleteDirective,
    AutoCompleteListComponent,
    BulkChipComponent,
    BulkChipInputComponent,
    TreeMultiSelectCheckboxComponent,
    ChipComponent,
    ChipInputComponent,
    ChipSingleComponent,
    ChipDropdownComponent,
    ChoiceFieldComponent,
    DateTextFieldComponent,
    DeprecatedDatePickerComponent,
    DeprecatedIntFieldComponent,
    DeprecatedLabeledFieldComponent,
    DeprecatedSearchFieldComponent,
    DeprecatedSingleSelectDropdownComponent,
    DeprecatedSingleSelectDropdownFieldComponent,
    DeprecatedTextFieldComponent,
    EmptyOptionPipe,
    EnhancedSelectComponent,
    FileDropDirective,
    FormStatusDirective,
    FormStatusPipe,
    FuzzyHighlightComponent,
    InputAccessorDirective,
    InputFormStatusDirective,
    LabeledFieldHospitalComponent,
    MultiSelectComponent,
    MultiSelectDropdownComponent,
    MultiSelectDropdownFieldComponent,
    MultiSelectNodeComponent,
    MultiSelectNumberComparatorComponent,
    OptionFilterComponent,
    OptionsPipe,
    OptionSelectComponent,
    OptionSelectNodeComponent,
    OptionSelectNodeDirective,
    PercentValuePipe,
    PhoneMaskDirective,
    RangeSliderComponent,
    RangeSliderFieldComponent,
    SelectDropdownComponent,
    SelectFieldComponent,
    SingleSelectComponent,
    SingleSelectNodeComponent,
    SingleSelectNodeDirective,
    StringOptionsPipe,
    SubmitErrorComponent,
    TaxIdMaskDirective,
    TaxIdPipe,
    TextareaFieldComponent,
    TreeMultiSelectComponent,
    TreeMultiSelectDropdownComponent,
    TreeMultiSelectDropdownFieldComponent,
    TreeMultiSelectNodeComponent,
    ValidationErrorComponent,
  ],
  exports: [
    AddressInputComponent,
    AutocompleteDirective,
    AutoCompleteListComponent,
    BulkChipComponent,
    BulkChipInputComponent,
    TreeMultiSelectCheckboxComponent,
    ChipComponent,
    ChipInputComponent,
    ChipSingleComponent,
    ChipDropdownComponent,
    ChoiceFieldComponent,
    DateTextFieldComponent,
    DeprecatedDatePickerComponent,
    DeprecatedIntFieldComponent,
    DeprecatedLabeledFieldComponent,
    DeprecatedSearchFieldComponent,
    DeprecatedSingleSelectDropdownComponent,
    DeprecatedSingleSelectDropdownFieldComponent,
    DeprecatedTextFieldComponent,
    EmptyOptionPipe,
    EnhancedSelectComponent,
    FileDropDirective,
    FormStatusDirective,
    FormStatusPipe,
    FuzzyHighlightComponent,
    InputAccessorDirective,
    InputFormStatusDirective,
    LabeledFieldHospitalComponent,
    MultiSelectComponent,
    MultiSelectDropdownComponent,
    MultiSelectDropdownFieldComponent,
    MultiSelectNodeComponent,
    MultiSelectNumberComparatorComponent,
    OptionFilterComponent,
    OptionsPipe,
    OptionSelectComponent,
    OptionSelectNodeDirective,
    PercentValuePipe,
    PhoneMaskDirective,
    RangeSliderComponent,
    RangeSliderFieldComponent,
    SelectDropdownComponent,
    SelectFieldComponent,
    SingleSelectComponent,
    SingleSelectNodeComponent,
    SingleSelectNodeDirective,
    StringOptionsPipe,
    SubmitErrorComponent,
    TaxIdMaskDirective,
    TaxIdPipe,
    TextareaFieldComponent,
    TreeMultiSelectNodeComponent,
    TreeMultiSelectComponent,
    TreeMultiSelectDropdownComponent,
    TreeMultiSelectDropdownFieldComponent,
    ValidationErrorComponent,
  ],
  imports: [
    AngularCommonModule,
    AvatarModule,
    CalloutModule,
    ComponentsModule,
    CoreModule,
    FormsModule,
    InteractionModule,
    LoadModule,
    ObserversModule,
    RivModule,
    ScrollModule,
    StateModule,
    DragDropModule,
    TranslationModule,
  ],
})
export class InputModule {}
